// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { clamp } from "lodash";
import React, { useState } from "react";

import { calculateAgeAtDate } from "../../utils/utils";
import { Dropdown } from "../CaseDetails/Form/Elements/Dropdown";
import { SharedDatePicker } from "../shared/SharedDatePicker";
import { Banner } from "../shared/styles/Banner";
import {
  Checkbox,
  CheckboxContainer,
  CheckboxLabel,
} from "../shared/styles/CheckboxStyles";
import { CancelButton, SaveButton } from "../shared/styles/FormButtonStyles";
import {
  AssessmentTypeDisplayNames,
  AssessmentTypeKey,
  ORAS_TOOL_KEYS,
} from "./assessmentTypeUtils";
import * as DomainCardStyled from "./DomainCard.styles";
import {
  DatePickerWrapper,
  dropdownStyles,
  FieldContainer,
  Input,
  Label,
  ORASTitle,
  Textarea,
} from "./FormComponents.styles";
import * as ModalStyled from "./ModalStyles";
import {
  AGE_POINT_MAX,
  DomainConfig,
  getAgeAtAssessmentPoint,
  getDomainsForAssessmentType,
  ORAS_EMPTY_FORM,
  ORASFormData,
} from "./utils";

const ASSESSMENT_TYPE_OPTIONS = ORAS_TOOL_KEYS.map((value) => ({
  value,
  label: AssessmentTypeDisplayNames[value],
}));

type SelectOption = { label: string; value: string };

interface ORASFormProps {
  onCancel: () => void;
  onSave: (data: ORASFormData) => Promise<void>;
  initialData?: ORASFormData | null;
  birthDate: Date | null;
}

export const ORASForm: React.FC<ORASFormProps> = ({
  onCancel,
  onSave,
  initialData,
  birthDate,
}) => {
  const [formData, setFormData] = useState<ORASFormData>(
    initialData ?? ORAS_EMPTY_FORM,
  );
  // false = user explicitly skipped; null/true = entering scores
  const isSkipped = formData.ORASDomainsAvailable === false;
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const domains = getDomainsForAssessmentType(formData.assessmentType);
  const scoredDomains = domains.filter((d) => d.scoreField);
  const includesAgePoint = domains.some((d) => d.includesAgePoint);
  const ageAtAssessment =
    birthDate && formData.assessmentDate
      ? calculateAgeAtDate(birthDate, formData.assessmentDate)
      : null;
  const ageLevel = includesAgePoint
    ? getAgeAtAssessmentPoint(ageAtAssessment)
    : null;
  const totalScore = scoredDomains.reduce<number>(
    (sum, d) =>
      sum + ((formData[d.scoreField as keyof ORASFormData] as number) ?? 0),
    ageLevel ?? 0,
  );
  const maxTotalScore = scoredDomains.reduce(
    (sum, d) => sum + (d.maxScore ?? 0),
    0,
  );
  const allScoresFilled =
    scoredDomains.length === 0 ||
    scoredDomains.every(
      (d) => formData[d.scoreField as keyof ORASFormData] != null,
    );
  const hasAnyScore = scoredDomains.some(
    (d) => formData[d.scoreField as keyof ORASFormData] != null,
  );

  const handleCancel = () => {
    setFormData(ORAS_EMPTY_FORM);
    setSaveError(null);
    onCancel();
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave({
        ...formData,
        assessmentScore: isSkipped ? null : totalScore,
        ageLevel: isSkipped ? null : ageLevel,
      });
      handleCancel();
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const requiredFieldsFilled =
    !!formData.assessmentAdministeredBy?.trim() &&
    !!formData.assessmentType &&
    !!formData.assessmentDate &&
    (isSkipped ? !!formData.noORASDomainReason?.trim() : allScoresFilled);

  return (
    <DomainCardStyled.CardContainer>
      <DomainCardStyled.HeaderSection>
        <ORASTitle>ORAS Assessment</ORASTitle>
      </DomainCardStyled.HeaderSection>
      <Banner>
        Manually entering assessment data will overwrite any current or future
        data synced from the ORAS system.
      </Banner>

      <DomainCardStyled.ContentArea>
        <FieldContainer>
          <Label>Select an Assessment Type</Label>
          <Dropdown
            value={
              formData.assessmentType
                ? {
                    value: formData.assessmentType,
                    label: AssessmentTypeDisplayNames[formData.assessmentType],
                  }
                : null
            }
            options={ASSESSMENT_TYPE_OPTIONS}
            onChange={(option) => {
              const newAssessmentType =
                ((option as SelectOption)?.value as AssessmentTypeKey) ?? null;
              if (newAssessmentType === formData.assessmentType) return;
              setFormData({
                ...ORAS_EMPTY_FORM,
                assessmentDate: formData.assessmentDate,
                assessmentAdministeredBy: formData.assessmentAdministeredBy,
                assessmentType: newAssessmentType,
              });
            }}
            placeholder="Select"
            styles={dropdownStyles}
          />
        </FieldContainer>

        <FieldContainer>
          <Label>Assessment Date</Label>
          <DatePickerWrapper>
            <SharedDatePicker
              selected={formData.assessmentDate ?? null}
              onChange={(date) =>
                setFormData({ ...formData, assessmentDate: date ?? null })
              }
              placeholder="--"
            />
          </DatePickerWrapper>
        </FieldContainer>

        <FieldContainer>
          <Label>Administered By</Label>
          <Input
            value={formData.assessmentAdministeredBy ?? ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                assessmentAdministeredBy: e.target.value || null,
              })
            }
          />
        </FieldContainer>

        <FieldContainer>
          <DomainCardStyled.SpacedRow>
            <Label>Domains</Label>
            {formData.assessmentType && (
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  id="skip-domains"
                  checked={isSkipped}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData((prev) => ({
                      ...prev,
                      ORASDomainsAvailable: !checked,
                      ...(checked
                        ? Object.fromEntries(
                            scoredDomains.map((d) => [d.scoreField, null]),
                          )
                        : {}),
                    }));
                  }}
                />
                <CheckboxLabel>Skip entering scores</CheckboxLabel>
              </CheckboxContainer>
            )}
          </DomainCardStyled.SpacedRow>
          {isSkipped ? (
            <Textarea
              value={formData.noORASDomainReason ?? ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  noORASDomainReason: e.target.value || null,
                })
              }
              placeholder="Provide a reason for skipping scores"
            />
          ) : (
            <DomainCardStyled.HelperText>
              {formData.assessmentType
                ? "Enter the numerical values for each domain"
                : "Waiting for assessment type"}
            </DomainCardStyled.HelperText>
          )}
          {includesAgePoint && !isSkipped && (
            <DomainCardStyled.SpacedRow>
              <DomainCardStyled.DomainEntryLabel>
                <DomainCardStyled.DomainEntryTitle>
                  Age at Assessment
                </DomainCardStyled.DomainEntryTitle>
                {ageAtAssessment != null && (
                  <DomainCardStyled.HelperText>
                    Age {ageAtAssessment}, calculated from birthdate on file
                  </DomainCardStyled.HelperText>
                )}
              </DomainCardStyled.DomainEntryLabel>
              <DomainCardStyled.DomainRow>
                <DomainCardStyled.ReadOnlyScore>
                  {ageLevel ?? "--"}
                </DomainCardStyled.ReadOnlyScore>
                <DomainCardStyled.ORASDomainText>
                  / {AGE_POINT_MAX}
                </DomainCardStyled.ORASDomainText>
              </DomainCardStyled.DomainRow>
            </DomainCardStyled.SpacedRow>
          )}
          {domains
            .filter(
              (
                domain,
              ): domain is DomainConfig & { scoreField: keyof ORASFormData } =>
                domain.scoreField !== undefined,
            )
            .map((domain) => {
              // The age point has its own row above, so the input covers only
              // the domain's remaining items
              const inputMaxScore =
                domain.maxScore !== undefined && domain.includesAgePoint
                  ? domain.maxScore - AGE_POINT_MAX
                  : domain.maxScore;
              return (
                <DomainCardStyled.SpacedRow key={domain.key}>
                  <DomainCardStyled.DomainEntryTitle>
                    {domain.title}
                  </DomainCardStyled.DomainEntryTitle>
                  {!isSkipped && (
                    <DomainCardStyled.DomainRow>
                      <Input
                        $shrink
                        type="number"
                        value={
                          (formData[domain.scoreField] as number | null) ?? ""
                        }
                        onChange={(e) => {
                          const raw = e.target.value
                            ? Number(e.target.value)
                            : null;
                          const clamped =
                            raw !== null
                              ? clamp(raw, 0, inputMaxScore ?? raw)
                              : null;
                          setFormData({
                            ...formData,
                            [domain.scoreField]: clamped,
                          });
                        }}
                      />
                      <DomainCardStyled.ORASDomainText>
                        / {inputMaxScore}
                      </DomainCardStyled.ORASDomainText>
                    </DomainCardStyled.DomainRow>
                  )}
                </DomainCardStyled.SpacedRow>
              );
            })}

          {!isSkipped && (
            <DomainCardStyled.SpacedRow>
              <DomainCardStyled.DomainEntryLabel>
                <DomainCardStyled.DomainEntryTitle>
                  Total Score
                </DomainCardStyled.DomainEntryTitle>
                {includesAgePoint && (
                  <DomainCardStyled.HelperText>
                    Includes age at assessment
                  </DomainCardStyled.HelperText>
                )}
              </DomainCardStyled.DomainEntryLabel>
              <DomainCardStyled.DomainRow $center>
                <DomainCardStyled.ORASDomainText>
                  {hasAnyScore ? `${totalScore} / ${maxTotalScore}` : "--"}
                </DomainCardStyled.ORASDomainText>
              </DomainCardStyled.DomainRow>
            </DomainCardStyled.SpacedRow>
          )}
        </FieldContainer>

        {saveError && (
          <ModalStyled.ErrorMessage>{saveError}</ModalStyled.ErrorMessage>
        )}

        <DomainCardStyled.SpacedRow>
          <CancelButton onClick={handleCancel} disabled={isSaving}>
            Cancel
          </CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={!requiredFieldsFilled || isSaving}
          >
            {isSaving ? "Saving..." : "Continue"}
          </SaveButton>
        </DomainCardStyled.SpacedRow>
      </DomainCardStyled.ContentArea>
    </DomainCardStyled.CardContainer>
  );
};
